import {
  Activity, BarChart3, BookOpen, Bot, Building2, CalendarDays, ClipboardCheck, Clock, Copy, Database,
  Home, Inbox, LayoutDashboard, ListTodo, Mail, MailPlus, Megaphone, MessageSquare, MousePointerClick, Newspaper,
  Receipt, ScrollText, Search, Send, Snowflake, Sparkles, Star, Tag, TrendingUp, Trophy, UserCheck, Users,
  type LucideIcon,
} from 'lucide-react';
import { useOrganizerPortal } from '../../contexts/OrganizerPortalContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePendingCounts, PENDING_PATHS } from '../../hooks/usePendingCounts';
import { verwaltungsMenueFuer, VORSCHLAEGE_PFAD, DUPLIKATE_PFAD } from '@radevents/shared';

/**
 * Das Menü der Verwaltung an einer Stelle: Abschnitte, Symbole und Zähler. Gelesen von der
 * Seitenleiste (Desktop, auf dem Handy als „Mehr") und von der Tab-Leiste unten auf dem Handy
 * (`VerwaltungTabLeiste`). Zwei Listen gingen auseinander, sobald ein Menüpunkt dazukommt.
 */

// Die Reiter-Seiten haben einen Eintrag im Menue; die Badge summiert ihre Reiter.
export const VORSCHLAEGE = VORSCHLAEGE_PFAD;
export const DUPLIKATE = DUPLIKATE_PFAD;
const VORSCHLAEGE_TABS = [PENDING_PATHS.eventEdits, PENDING_PATHS.organizerEdits, PENDING_PATHS.imageProposals];
const DUPLIKATE_TABS = [PENDING_PATHS.eventDuplicates, PENDING_PATHS.organizerDuplicates];

/** Ein Menuepunkt ist aktiv, wenn der Pfad exakt passt oder unter einer Reiter-Seite liegt. */
export const isActivePath = (itemPath: string, pathname: string): boolean =>
  pathname === itemPath || ((itemPath === VORSCHLAEGE || itemPath === DUPLIKATE) && pathname.startsWith(itemPath + '/'));

/**
 * Ein Symbol je Menüpunkt, alle aus `lucide-react` (ein Strichstil). Bis zum 26.09.2026 standen hier
 * 18 handgezeichnete Pfade, von denen sich vier Einträge dasselbe Symbol teilten (Personen für
 * Veranstalter, Zugänge, Nutzer und Veranstalter-Bereich; Balken für fünf Auswertungen).
 */
export const menuIcons: Record<string, LucideIcon> = {
  '/admin/start': Home,
  '/admin/overview': BarChart3,
  '/dashboard': LayoutDashboard,
  '/events/manage': CalendarDays,
  // Uhr statt Kalender: hier zaehlt der Zeitpunkt der Oeffnung, nicht der Veranstaltungstag.
  '/admin/anmeldestarts': Clock,
  [VORSCHLAEGE]: ClipboardCheck,
  '/organizers/zugaenge': UserCheck,
  '/veranstalter-bereich': Building2,
  [DUPLIKATE]: Copy,
  '/organizers': Building2,
  '/users': Users,
  '/audit-logs': ScrollText,
  '/enrichment-queue': ListTodo,
  '/agent-state': Bot,
  '/agent-runs': Activity,
  '/event-sources': Database,
  '/events/most-favorited': Star,
  '/admin/feedback': MessageSquare,
  '/admin/anfragen': Inbox,
  '/admin/buchungen': Receipt,
  '/admin/preise': Tag,
  '/admin/werbeplatz': Megaphone,
  '/admin/statistiken': MousePointerClick,
  '/admin/auswertung': Search,
  '/admin/geschaeft': TrendingUp,
  '/admin/email': Mail,
  '/admin/outreach': Send,
  '/admin/rennkalender': Trophy,
  '/admin/news': Newspaper,
  '/admin/rundmail': MailPlus,
  '/admin/winterchallenge': Snowflake,
  '/admin/handbuch': BookOpen,
  '/admin/neuerungen': Sparkles,
};

export type MenueAbschnitt = {
  label?: string;
  items: { path: string; label: string }[];
  /** Beim ersten Besuch zugeklappt; danach zaehlt, was der Nutzer zuletzt eingestellt hat. */
  defaultClosed?: boolean;
};

export function useVerwaltungsMenue() {
  const { isAdminOrModerator } = useAuth();
  const pendingCounts = usePendingCounts(isAdminOrModerator());
  const { isOrganizer } = useOrganizerPortal();

  const { user } = useAuth();
  // Die Redaktionsabschnitte kommen aus dem gemeinsamen Menü (shared/src/verwaltung/menue.ts),
  // dasselbe, das die Startseite der Verwaltung in der App zeichnet.
  const redaktion: MenueAbschnitt[] = verwaltungsMenueFuer(user?.role).map(a => ({
    label: a.label,
    defaultClosed: a.zugeklappt,
    items: a.punkte.map(p => ({ path: p.pfad, label: p.label })),
  }));
  const [start, ...rest] = redaktion;
  const sections: MenueAbschnitt[] = [
    ...(!isAdminOrModerator() ? [{ items: [{ path: '/dashboard', label: 'Dashboard' }] }] : []),
    // Die Seite nach der Anmeldung: die Zahlen, auf die man zuerst schaut.
    ...(start ? [start] : []),
    // Nur fuer verknuepfte Veranstalter. Steht bewusst weit oben: fuer sie ist das die
    // eigentliche Arbeitsflaeche, alles darunter sieht ein normales Konto ohnehin nicht.
    ...(isOrganizer ? [{
      label: 'Mein Veranstalter',
      items: [{ path: '/veranstalter-bereich', label: 'Events & Stammdaten' }],
    }] : []),
    ...rest,
  ];

  /** Badge eines Menuepunkts; Reiter-Seiten summieren ihre Reiter. */
  const countFor = (path: string): number => {
    const tabs = path === VORSCHLAEGE ? VORSCHLAEGE_TABS : path === DUPLIKATE ? DUPLIKATE_TABS : [path];
    return tabs.reduce((sum, p) => sum + (pendingCounts[p] ?? 0), 0);
  };

  return { sections, countFor, isOrganizer };
}
